import { memo, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronUp, Circle, CircleCheck, CircleMinus, CirclePause, ListTodo, Loader2 } from 'lucide-react'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { getChatState, UiChatStatus } from '@/state/useAgentStore'
import type { AgentPlan, AgentPlanTask, PlanExtractor } from '@/config/types'
import type { AgentMessage } from '@multiplayer-app/ai-agent-types'

/** Pinned above the composer when the host config provides a `planExtractor`
 * and the active chat carries plan snapshots in its tool calls. Collapsed:
 * one row with title, progress, and the task in flight. Expanded: the full
 * task list. The newest snapshot always wins, so the panel updates in place
 * as the agent works through the plan. */

function latestPlan(messages: AgentMessage[], pendingAssistant: AgentMessage | undefined, extract: PlanExtractor): AgentPlan | undefined {
  const ordered = [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  if (pendingAssistant) ordered.push(pendingAssistant)
  for (let m = ordered.length - 1; m >= 0; m--) {
    const toolCalls = ordered[m].toolCalls ?? []
    for (let c = toolCalls.length - 1; c >= 0; c--) {
      try {
        const plan = extract(toolCalls[c])
        if (plan && Array.isArray(plan.tasks) && plan.tasks.length > 0) return plan
      } catch {
        // A bad snapshot must never take down the chat window.
      }
    }
  }
  return undefined
}

const TaskStatusIcon = ({ status, generating }: { status: AgentPlanTask['status']; generating: boolean }) => {
  switch (status) {
    case 'done':
      return <CircleCheck size={13} className='mp-agent-plan-task-icon mp-agent-plan-task-icon-done' />
    case 'in_progress':
      // A task is only genuinely "running" while the chat is generating.
      // After a stop/abort/finish, plan bookkeeping may lag — render paused,
      // never a live spinner that suggests work is still happening.
      return generating ? (
        <Loader2 size={13} className='mp-agent-plan-task-icon mp-agent-subagent-spinner' />
      ) : (
        <CirclePause size={13} className='mp-agent-plan-task-icon' />
      )
    case 'skipped':
      return <CircleMinus size={13} className='mp-agent-plan-task-icon' />
    default:
      return <Circle size={13} className='mp-agent-plan-task-icon' />
  }
}

export const PlanProgressPanel = memo(() => {
  const runtime = useAgentRuntime()
  const extract = runtime.config.planExtractor
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const messages = useAgentStore((state) => (activeChatId ? getChatState(state, activeChatId).messages : undefined))
  const pendingAssistant = useAgentStore((state) =>
    activeChatId ? getChatState(state, activeChatId).pendingAssistant : undefined
  )
  const generating = useAgentStore((state) =>
    activeChatId ? getChatState(state, activeChatId).status === UiChatStatus.Streaming : false
  )
  const [isExpanded, setIsExpanded] = useState(false)

  const plan = useMemo(
    () => (extract && messages ? latestPlan(messages, pendingAssistant, extract) : undefined),
    [extract, messages, pendingAssistant]
  )

  if (!plan) return null

  const doneCount = plan.tasks.filter((task) => task.status === 'done' || task.status === 'skipped').length
  const currentTask = plan.tasks.find((task) => task.status === 'in_progress')
  const isComplete = doneCount === plan.tasks.length

  return (
    <div className='mp-agent-plan-panel-wrapper'>
      <div className='mp-agent-plan-panel'>
        <button
          type='button'
          className='mp-agent-plan-header'
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
        >
          <span className='mp-agent-plan-header-icon'>
            {isComplete ? <Check size={14} /> : <ListTodo size={14} />}
          </span>
          <span className='mp-agent-plan-title'>{plan.title || 'Plan'}</span>
          <span className='mp-agent-plan-progress'>
            {doneCount}/{plan.tasks.length}
          </span>
          {!isExpanded && currentTask && (
            <span className='mp-agent-plan-current'>
              {generating ? (
                <Loader2 size={12} className='mp-agent-subagent-spinner' />
              ) : (
                <CirclePause size={12} />
              )}
              <span className='mp-agent-plan-current-title'>
                {currentTask.title}
                {!generating && ' (paused)'}
              </span>
            </span>
          )}
          <span className='mp-agent-plan-chevron'>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </span>
        </button>
        {isExpanded && (
          <ul className='mp-agent-plan-tasks'>
            {plan.tasks.map((task) => (
              <li
                key={task.id}
                className={`mp-agent-plan-task mp-agent-plan-task-${task.status}`}
              >
                <TaskStatusIcon status={task.status} generating={generating} />
                <span className='mp-agent-plan-task-title'>{task.title}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
})

PlanProgressPanel.displayName = 'PlanProgressPanel'

export default PlanProgressPanel
